<template>
  <div class="do-smartscreen" :style="computedStyle">
    <div class="do-smartscreen__header">
      <div class="do-smartscreen__header__left">
        <h4>{{ dateFormatted }}</h4>
      </div>
      <div class="do-smartscreen__header__right">
        <label class="d-label">
          Senest opdateret: {{ latestUpdate }}
        </label>
      </div>
    </div>
    <div class="table-container">
      <table class="d-table">
        <thead>
        <tr>
          <th>Tidspunkt</th>
          <th>Lokale</th>
          <th>Lærer</th>
          <th>Elever</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="eventsWithProps.length === 0">
          <th class="text-explainer">Ingen lektioner</th>
        </tr>
        <tr v-for="event in eventsWithProps">
          <td>{{ event.vEventTime }} {{ event.title }}</td>
          <td>
            {{ event.vRooms }}
          </td>
          <td>
            {{ event.vTeachers }}
          </td>
          <td>{{ event.vStudents }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import docendo from '../../../docendo'

export default {
  props: {
    calendarId: {
      default: 285799
    }
  },
  data () {
    return {
      date: moment(),
      events: [],
      isLoadingEvents: false,
      refreshTimeout: null,
      fontSize: '16px',
      scrollInterval: null
    }
  },
  computed: {
    computedStyle () {
      return {
        fontSize: this.fontSize
      }
    },
    dateFormatted () {
      return moment(this.date).format('dddd LL')
    },
    latestUpdate () {
      return moment(this.date).format('LL LTS')
    },
    eventsFiltered () {
      return this.events.filter(event => moment(event.start).locale('en').format('YYYY-MM-DD') === moment(this.date).locale('en').format('YYYY-MM-DD'))
    },
    eventsSorted () {
      return this.eventsFiltered.sort((a, b) => {
        return moment(a.start).unix() - moment(b.start).unix()
      })
    },
    eventsWithProps () {
      return this.eventsSorted.map(event => {
        return {
          ...event,
          vEventTime: moment(event.start).format('LT') + ' - ' + moment(event.end).format('LT'),
          vRooms: event.calendars
            .filter(cal => cal.calendar_type === 'room')
            .map(cal => cal.name)
            .join(', '),
          vTeachers: event.calendars
            .filter(cal => cal.calendar_type === 'user')
            .map(cal => cal.name)
            .join(', '),
          vStudents: event.calendars
            .filter(cal => cal.calendar_type === 'student')
            .map(cal => cal.name)
            .join(', ')
        }
      })
    }
  },
  watch: {
    events (val) {
      this.$nextTick(_ => {
        this.checkForUrlParamScroll()
      })
    }
  },
  methods: {
    checkScrollable () {
      const publicAppElement = document.querySelector('.public-app')
      const viewportHeight = window.innerHeight

      // Check if the .public-app element is taller than the viewport
      if (publicAppElement.offsetHeight > viewportHeight) {
        console.log('The .public-app element is taller than the viewport and can be scrolled.')

        this.startAutoScroll()
      } else {
        console.log('The .public-app element is not taller than the viewport and cannot be scrolled.')
      }
    },
    startAutoScroll () {
      let scrollDirection = 1
      let scrollSpeed = 1

      const autoScrollPage = () => {
        window.scrollBy(0, scrollSpeed * scrollDirection)

        // Reverse direction if we've reached the bottom or top of the page
        if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
          scrollDirection = -1
        } else if (window.scrollY <= 0) {
          scrollDirection = 1
        }
      }
      clearInterval(this.scrollInterval)
      this.scrollInterval = setInterval(autoScrollPage, 30)
    },
    setupRefresh () {
      this.getCalendarEvents()

      this.refreshTimeout = setInterval(() => {
        this.date = moment()
        this.getCalendarEvents()
      }, 600000) // 600,000 milliseconds = 10 minutes
    },
    getCalendarEvents () {
      this.isLoadingEvents = true
      const startDate = moment(this.date)
      const endDate = moment(this.date).add(1, 'days')
      docendo.publicEventsFromCalendar(this.calendarId, startDate, endDate, success => {
        this.events = success && success.length > 0 ? JSON.parse(success) : []
        this.isLoadingEvents = false
        this.$nextTick(_ => {
          // this.checkScrollable()
        })
      }, failure => {
        console.log({failure})
      })
    },
    updateFontSizeFromUrl () {
      // Extract the fragment part of the URL
      const hash = window.location.hash

      // Check if there's a query string after the hash
      if (hash.includes('?')) {
        const queryString = hash.split('?')[1]
        const urlParams = new window.URLSearchParams(queryString)
        const fontsize = urlParams.get('fontsize')

        if (fontsize) {
          this.fontSize = `${fontsize}px`
        }
      }
    },
    checkForUrlParamScroll () {
      const hash = window.location.hash

      // Check if there's a query string after the hash
      if (hash.includes('?')) {
        const queryString = hash.split('?')[1]
        const urlParams = new window.URLSearchParams(queryString)
        const autoscroll = urlParams.get('autoscroll')

        // Check if autoscroll is set to 'true'
        if (autoscroll && autoscroll === 'true') {
          console.log('Autoscroll is enabled')

          // Call a method to start auto-scrolling if needed
          this.checkScrollable()
        } else {
          console.log('Autoscroll is disabled')
        }
      }
    }
  },
  mounted () {
    this.setupRefresh()
    this.updateFontSizeFromUrl()
  },
  beforeDestroy () {
    clearInterval(this.refreshTimeout)
  }
}
</script>

<style scoped>

.do-smartscreen {
  padding: 1rem;
}

.do-smartscreen__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.do-smartscreen__header__right,
.do-smartscreen__header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.do-smartscreen__header__left h4 {
  text-transform: capitalize;
  margin-bottom: 1rem;
}

.d-table {
  background-color: #fff;
  width: 100%;
  border: 1px solid #e2e8f0;
  border-collapse: collapse;
}

.d-table thead {
  border: 1px solid #e2e8f0;
  text-transform: uppercase;
  padding: 1rem;
  font-weight: 700;
  text-align: left;
  background: #f6f9fc;
  color: #8898aa;
}

.d-table tbody tr:nth-child(even) {
  background: rgb(246, 249, 252);
}

.d-table tbody td,
.d-table thead th {
  line-height: 1.5rem;
  border: 1px solid #e2e8f0;
  padding: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// SmartScreen.vue?c9b49f14